import {
  SelectProps as AntSelectProps,
  BaseOptionType,
  DefaultOptionType,
} from "antd/es/select";
import React, { useEffect, useMemo } from "react";
import styled from "styled-components";
import FlatList from "common/Lists/FlatList";
import Flex from "common/Boxes/Flex";
import { toggleArrayItem } from "@shared/utils/arrays";
import { useVirtualKeyboard } from "utils/hooks/misc";
import BottomDrawer from "common/Drawers/BottomDrawer";
import Text from "common/Typography/Text";
import { typography } from "@shared/common/Themes/typography";
import { Check } from "common/Icons/Icons";
import LongPress from "common/Gestures/LongPress.tsx";
import { useSelectSearch } from "common/Select/hooks";
import useBackButtonHandler from "utils/hooks/useBackButtonHandler.ts";
import { useTelegramSafeAreaInsets } from "utils/hooks/useTelegramWebApp.ts";

interface MobileSelectProps<
  ValueType = any,
  OptionType extends BaseOptionType = DefaultOptionType
> extends Omit<
    AntSelectProps<ValueType, OptionType>,
    "showSearch" | "filterOption" | "optionFilterProp"
  > {
  mobileTitle?: string;
  bottomExtraElement?: React.JSX.Element;
  titleRightElement?: React.JSX.Element;
  zIndex?: number;
  showSearch?: boolean | SearchConfig<OptionType>;
}

const MobileSelect = <
  ValueType = any,
  OptionType extends BaseOptionType = DefaultOptionType
>({
  options,
  value,
  open,
  onOpenChange,
  onChange,
  mode,
  mobileTitle,
  showSearch: propsShowSearch,
  bottomExtraElement,
  titleRightElement,
  zIndex,
}: MobileSelectProps<ValueType, OptionType>) => {
  const showSearchEnabled =
    typeof propsShowSearch === "boolean"
      ? propsShowSearch
      : propsShowSearch !== undefined
      ? true
      : (options?.length || 0) > 10;

  const searchConfig =
    typeof propsShowSearch === "object" ? propsShowSearch : {};
  const filterOption = searchConfig.filterOption ?? true;
  const optionFilterProp = searchConfig.optionFilterProp;

  const { keyboardHeight } = useVirtualKeyboard();
  const { safeAreaInset } = useTelegramSafeAreaInsets();
  const {
    setSearchValue,
    setIsSearch,
    searchComponent,
    searchValue,
    isSearch,
  } = useSelectSearch();
  const { registerBackButtonListener } = useBackButtonHandler();

  useEffect(() => {
    if (!open) {
      setSearchValue("");
      setIsSearch(false);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      return registerBackButtonListener(() => {
        closeSelect();
        return true;
      });
    }
  }, [open]);

  const valuesSet = useMemo(() => {
    if (!value) return new Set();
    return new Set(Array.isArray(value) ? value : [value]);
  }, [value]);

  const onOptionClick = (
    item: OptionType,
    isLongPress?: boolean,
    isGroup?: boolean
  ) => {
    if (isGroup) return;
    const isMultiple =
      (mode === "multiple" || mode === "tags") && Array.isArray(value);
    if (!isMultiple) {
      closeSelect();
    }
    const updatedValue =
      isMultiple && !isLongPress
        ? toggleArrayItem(value ?? [], item.value)
        : item.value;
    onChange?.(updatedValue, item);
  };

  const closeSelect = () => onOpenChange?.(false);

  const renderOption = ({ item }: { item: OptionType }) => {
    const selected = valuesSet.has(item.value);
    const isGroup = !!item.options;
    const option = (
      <Option
        onClick={() => onOptionClick(item, false, isGroup)}
        $selected={selected}
      >
        <LabelWrapper>
          {item.children ?? (
            <Text
              as="div"
              type={isGroup ? "secondary" : "primary"}
              $size={typography.fontSizes[isGroup ? "S" : "M"]}
            >
              {item.label}
            </Text>
          )}
        </LabelWrapper>
        {selected && <CheckIcon />}
      </Option>
    );
    return (
      <LongPress
        triggerElement={option}
        onLongPress={() => onOptionClick(item, true, isGroup)}
      />
    );
  };

  const filterOptionBySearch = (option: OptionType) => {
    if (typeof filterOption === "function") {
      return filterOption(searchValue || "", option);
    } else if (filterOption) {
      const filterProp = Array.isArray(optionFilterProp)
        ? optionFilterProp[0]
        : optionFilterProp ?? "label";
      const optionSearchValue: any = option[filterProp as keyof OptionType];
      return (
        !!optionSearchValue &&
        typeof optionSearchValue === "string" &&
        optionSearchValue
          ?.toLowerCase()
          .includes(searchValue?.toLowerCase() || "")
      );
    }
  };

  const ungroupedOptions = useMemo(() => {
    return options?.reduce((filteredOptions: OptionType[], option) => {
      filteredOptions.push(option);
      if (option.options) {
        filteredOptions = filteredOptions.concat(option.options);
      }
      return filteredOptions;
    }, []);
  }, [options]);

  const filteredOptions = useMemo(() => {
    if (!isSearch || !searchValue) return ungroupedOptions;
    return ungroupedOptions?.reduce((filteredOptions: OptionType[], option) => {
      if (option.options) {
        const groupOption = {
          ...option,
          options: option.options.filter(filterOptionBySearch),
        };
        if (groupOption.options?.length) {
          filteredOptions.push(groupOption);
        }
      } else if (filterOptionBySearch(option)) {
        filteredOptions.push(option);
      }
      return filteredOptions;
    }, []);
  }, [ungroupedOptions, searchValue, isSearch]);

  const maxHeight = `calc(40vh + ${keyboardHeight}px + ${safeAreaInset.bottom}px)`;

  const content = (
    <FlatListWrapper>
      <FlatList
        data={filteredOptions || []}
        renderContent={renderOption}
        wrapperStyle={{
          maxHeight,
        }}
      />
    </FlatListWrapper>
  );

  return (
    <BottomDrawer
      title={mobileTitle}
      rightButtons={showSearchEnabled ? searchComponent : titleRightElement}
      content={content}
      open={open}
      onClose={closeSelect}
      contentStyle={{ padding: 0 }}
      footer={bottomExtraElement}
      zIndex={zIndex}
    />
  );
};

export default MobileSelect;

type FilterFunc<OptionType> = (
  inputValue: string,
  option?: OptionType
) => boolean;

interface SearchConfig<OptionType> {
  filterOption?: boolean | FilterFunc<OptionType>;
  optionFilterProp?: string | string[];
}

// A flex column so the list's own scroller shrinks to the capped sheet.
const FlatListWrapper = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;
const Option = styled(Flex)<{ $selected?: boolean }>`
  width: 100%;
  overflow: hidden;
  min-height: 22px;
  ${(p) =>
    p.theme.isNewLook &&
    `
    /* New look: 44px touch rows; the selected option reads in blue. */
    min-height: 28px;
    ${
      p.$selected
        ? `
      .ant-typography {
        color: ${p.theme.colors.primaryBlue};
        font-weight: ${p.theme.typography.fontWeights.MEDIUM};
      }
    `
        : ""
    }
  `}
`;
const LabelWrapper = styled.div`
  flex: 1;
  overflow: hidden;
`;
const CheckIcon = styled(Check)`
  color: ${(p) => p.theme.colors.primaryBlue};
  padding-left: 12px;
  height: 22px;
  width: 28px;
`;
